// Recrear video DENTRO de Recrear — ADR-2026-10-09-recrear-video-adentro-de-recrear.
//
// Recrear (`InterfazV2Plantillas` y `PlantillasBelleza`) suma un interruptor Imagen | Video
// al lado del título. Imagen es lo de siempre; Video cambia la grilla por la card "Recreá
// tu propio video" y las plantillas de video, que abren el modo `/recrear-video`. Sólo en
// la interfaz nueva (`uiNueva`): la vieja "Plantillas" no cambia.
//
// El medio vive en la URL (`?media=video`), como la categoría: el back desde el modo
// vuelve a Recrear en Video, y es con lo que llega la familia Videos del sheet de Generar.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronRight, Image, Sparkles, Video, X } from 'lucide-react';
// useState, ReactDOM, useEffect, useRef, ChevronRight, Sparkles, X se usan en PanelPlantilla y GrillaDeRecrearVideo.
import { ColumnasParallax } from '../redes/ColumnasParallax';
import { CardPlantillaDeVideo, CardSubirVideoParaRecrear, useSubidaDeVideoParaRecrear } from './CardsDeRecrearVideo';
import { PLANTILLAS_DE_RECREAR_VIDEO, type PlantillaDeRecrearVideo } from './plantillas';

export type MedioDeRecrear = 'imagen' | 'video';

const PARAM_MEDIA = 'media';

/** El medio de Recrear y cómo cambiarlo. Sin `uiNueva` es siempre Imagen. */
export function useMedioDeRecrear(uiNueva: boolean): {
  medio: MedioDeRecrear;
  cambiarMedio: (m: MedioDeRecrear) => void;
} {
  const location = useLocation();
  const navigate = useNavigate();
  const enLaUrl = new URLSearchParams(location.search).get(PARAM_MEDIA);
  const medio: MedioDeRecrear = uiNueva && enLaUrl === 'video' ? 'video' : 'imagen';
  const cambiarMedio = useCallback(
    (m: MedioDeRecrear) => {
      const params = new URLSearchParams(location.search);
      if (m === 'video') params.set(PARAM_MEDIA, 'video');
      else params.delete(PARAM_MEDIA);
      const qs = params.toString();
      // `replace`, como los chips: cambiar de medio no es una pantalla nueva.
      navigate(`${location.pathname}${qs ? `?${qs}` : ''}`, { replace: true });
    },
    [location.pathname, location.search, navigate],
  );
  return { medio, cambiarMedio };
}

const AZUL_RECREAR = '#1A3A8F';
/** Los primeros 6 videos de las plantillas, para el header. */
const TIRA_DEL_HEADER = PLANTILLAS_DE_RECREAR_VIDEO.slice(0, 6);

/** Header de Recrear con tira de fotos, luz azul y switch Imagen | Video. */
export const InterruptorDeMedio: React.FC<{
  medio: MedioDeRecrear;
  onCambiar: (m: MedioDeRecrear) => void;
}> = ({ medio, onCambiar }) => (
  <section
    className="relative overflow-hidden rounded-3xl"
    style={{ backgroundColor: '#2B5CD6' }}
  >
    {/* Tira de fotos */}
    <div className="relative w-full h-[140px] sm:h-[180px] overflow-hidden" aria-hidden>
      <div className="flex h-full w-full gap-1.5">
        {TIRA_DEL_HEADER.map((p, i) => (
          <div
            key={p.id}
            className={`h-full flex-1 overflow-hidden bg-white/[0.04] ${
              i >= 3 ? 'hidden sm:block' : ''
            }`}
          >
            <video
              src={`${p.videoUrl}#t=0.1`}
              muted
              playsInline
              preload="metadata"
              className="h-full w-full object-cover"
            />
          </div>
        ))}
      </div>
      {/* Fundido de las fotos al fondo — dos capas: una con el tono oscuro
          del fondo arriba y otra con el medio, para que no se vea el corte. */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background: 'linear-gradient(to bottom, transparent 0%, transparent 25%, #2B5CD6aa 50%, #2B5CD6 75%)',
        }}
      />
    </div>

    {/* Luz del primario que baja de las fotos */}
    <div
      aria-hidden
      className="pointer-events-none absolute left-1/2 -translate-x-1/2 rounded-[50%] blur-[80px] top-[100px] sm:top-[130px] h-[140px] w-[120%] opacity-25"
      style={{ background: 'var(--brand-primary-claro)' }}
    />

    {/* Contenido: título + switch */}
    <div className="relative z-10 -mt-8 px-5 pb-5 sm:-mt-10 sm:px-7 sm:pb-6">
      <h2 className="text-[24px] sm:text-[30px] font-bold leading-[1.1] tracking-[-0.02em] text-white">
        Recrear con plantillas
      </h2>
      <p className="mt-2 max-w-[44ch] text-[13.5px] sm:text-[14.5px] leading-snug text-white/50">
        Fotos y videos profesionales con tus productos y modelos, listos para publicar.
      </p>

      {/* Switch — segmented control con pastilla deslizante */}
      <div
        role="tablist"
        aria-label="Qué querés recrear"
        className="relative mt-4 flex h-[48px] rounded-xl bg-white/[0.15] p-1 select-none backdrop-blur-sm"
      >
        {/* Pastilla deslizante */}
        <span
          aria-hidden
          className={`absolute top-1 bottom-1 w-[calc(50%-4px)] rounded-[10px] bg-white shadow-[0_2px_8px_rgba(0,0,0,0.12)] transition-transform duration-300 ease-[cubic-bezier(0.34,1.4,0.64,1)]
            ${medio === 'video' ? 'translate-x-[calc(100%+8px)]' : 'translate-x-0'}`}
          style={{ left: 4 }}
        />
        {(['imagen', 'video'] as const).map((m) => {
          const activo = medio === m;
          return (
            <button
              key={m}
              type="button"
              role="tab"
              aria-selected={activo}
              onClick={() => onCambiar(m)}
              className={`relative z-[1] flex-1 flex items-center justify-center gap-2.5 rounded-[10px] text-[14.5px] font-semibold transition-colors duration-300
                ${activo
                  ? 'text-[#1A3A8F]'
                  : 'text-white/70 hover:text-white'}`}
            >
              {m === 'imagen' ? <Image className="w-[18px] h-[18px]" /> : <Video className="w-[18px] h-[18px]" />}
              {m === 'imagen' ? 'Imagen' : 'Video'}
            </button>
          );
        })}
      </div>
    </div>
  </section>
);

// ── Panel flotante de detalle de plantilla (portal, a la derecha de la grilla) ──

const PanelPlantilla: React.FC<{
  plantilla: PlantillaDeRecrearVideo;
  onUsar: () => void;
  onCerrar: () => void;
}> = ({ plantilla, onUsar, onCerrar }) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  // Cerrar con Escape.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onCerrar(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onCerrar]);

  // Click fuera del panel cierra.
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) onCerrar();
    };
    // Delay para que el click que abrió no cierre inmediatamente.
    const id = setTimeout(() => document.addEventListener('mousedown', handler), 50);
    return () => { clearTimeout(id); document.removeEventListener('mousedown', handler); };
  }, [onCerrar]);

  return ReactDOM.createPortal(
    <>
      {/* Backdrop sutil */}
      <div className="fixed inset-0 z-[60] bg-black/20 backdrop-blur-[2px] transition-opacity" />

      {/* Panel */}
      <div
        ref={panelRef}
        className="fixed right-0 top-0 bottom-0 z-[61] w-full sm:w-[400px] lg:w-[420px] bg-white shadow-[-8px_0_32px_rgba(0,0,0,0.12)] overflow-y-auto animate-slide-in-right"
        style={{ '--slide-from': '100%' } as React.CSSProperties}
      >
        {/* Botón cerrar */}
        <button
          type="button"
          onClick={onCerrar}
          className="absolute right-4 top-4 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-black/50 text-white backdrop-blur-sm hover:bg-black/70 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Video en loop */}
        <div className="relative aspect-[9/16] w-full bg-neutral-900">
          <video
            ref={videoRef}
            src={`${plantilla.videoUrl}#t=0.1`}
            muted
            loop
            autoPlay
            playsInline
            preload="auto"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <span className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/60 to-transparent" />
        </div>

        {/* Contenido */}
        <div className="px-5 py-5 flex flex-col gap-5">
          {/* Título y bajada */}
          <div>
            <h2 className="text-[22px] font-bold text-brand-black leading-tight">{plantilla.titulo}</h2>
            <p className="text-[15px] text-[#3C3C43]/60 mt-1 leading-snug">{plantilla.bajada}</p>
          </div>

          {/* Lo que pide */}
          <div className="flex flex-col gap-2">
            <p className="text-[12px] font-semibold text-[#3C3C43]/40 uppercase tracking-wide">Vas a necesitar</p>
            {plantilla.pide.map((p) => (
              <div key={p.rol} className="flex items-center gap-3 rounded-xl bg-[#F3F4F7] px-3.5 py-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white">
                  <ChevronRight className="w-4 h-4 text-[#3C3C43]/30" />
                </span>
                <span className="text-[14px] font-medium text-brand-black">{p.texto}</span>
              </div>
            ))}
          </div>

          {/* Indicaciones prearmadas */}
          {plantilla.indicaciones && (
            <div className="flex items-start gap-2.5 rounded-xl bg-[#F7F3FF] px-4 py-3">
              <Sparkles className="w-4 h-4 text-brand-primary shrink-0 mt-0.5" />
              <p className="text-[13px] text-[#3C3C43]/70 leading-snug">{plantilla.indicaciones}</p>
            </div>
          )}

          {/* Botón */}
          <button
            type="button"
            onClick={onUsar}
            className="w-full rounded-2xl bg-brand-primary text-white text-[16px] font-semibold py-4 transition-colors hover:bg-brand-primary-hover active:scale-[0.99]"
          >
            Usar esta plantilla
          </button>
        </div>
      </div>
    </>,
    document.body,
  );
};

/** La grilla de Recrear en Video: subir uno propio y las plantillas. Sin parallax.
 *  Clickear una plantilla navega directo a `/recrear-video` con la plantilla elegida. */
export const GrillaDeRecrearVideo: React.FC<{ quieto: boolean }> = () => {
  const { subiendo, error, abrirSelector, abrirPlantilla, inputDeArchivo } = useSubidaDeVideoParaRecrear();

  return (
    <div key="video" className="animate-category-swap">
      {inputDeArchivo}
      <ColumnasParallax
        quieto
        items={[
          <CardSubirVideoParaRecrear
            key="subir"
            aspectClass="aspect-[9/16]"
            subiendo={subiendo}
            error={error}
            onClick={abrirSelector}
          />,
          ...PLANTILLAS_DE_RECREAR_VIDEO.map((p) => (
            <CardPlantillaDeVideo
              key={p.id}
              plantilla={p}
              onClick={() => abrirPlantilla(p)}
            />
          )),
        ]}
      />
    </div>
  );
};
